import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, FileText, PartyPopper, HelpCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusUpdateDialog = ({ open, onOpenChange, listing, onSuccess }) => {
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const statusOptions = [
    {
      value: "still_active",
      label: "Still For Sale",
      icon: CheckCircle2,
      color: "bg-green-500 hover:bg-green-600",
      description: "My home is still actively for sale"
    },
    {
      value: "under_contract",
      label: "Under Contract",
      icon: FileText,
      color: "bg-yellow-500 hover:bg-yellow-600",
      description: "I have accepted an offer and am under contract"
    },
    {
      value: "sold",
      label: "SOLD!",
      icon: PartyPopper,
      color: "bg-blue-500 hover:bg-blue-600",
      description: "My home has sold successfully"
    },
    {
      value: "need_help",
      label: "Need Help",
      icon: HelpCircle,
      color: "bg-orange-500 hover:bg-orange-600",
      description: "I need assistance selling my home"
    }
  ];

  const handleSubmit = async () => {
    if (!selectedStatus) {
      toast.error("Please select a status");
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(
        `${API}/listings/${listing.id}/update-status`,
        {
          listing_id: listing.id,
          status: selectedStatus,
          notes: notes || undefined
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        onOpenChange(false);
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedStatus(null);
    setNotes("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="text-2xl">Update Listing Status</DialogTitle>
          <p className="text-sm text-gray-600 mt-2">
            {listing?.title || "Your listing"}
          </p>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <p className="text-sm text-gray-700">
            Please let us know the current status of your home:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {statusOptions.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  onClick={() => setSelectedStatus(option.value)}
                  className={`
                    relative p-4 rounded-lg border-2 transition-all text-left
                    ${selectedStatus === option.value
                      ? "border-orange-500 bg-orange-50"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                    }
                  `}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg text-white ${option.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">
                        {option.label}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {option.description}
                      </div>
                    </div>
                  </div>
                  {selectedStatus === option.value && (
                    <div className="absolute top-2 right-2">
                      <CheckCircle2 className="w-5 h-5 text-orange-500" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {selectedStatus && (
            <div className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <label className="text-sm font-medium text-gray-700">
                Additional Notes (Optional)
              </label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional information you'd like to share..."
                className="min-h-[80px]"
              />
            </div>
          )}

          {selectedStatus === "sold" && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
              <p className="font-semibold text-blue-900 mb-1">
                🎉 Congratulations on selling your home!
              </p>
              <p className="text-blue-700">
                Your listing will be marked as sold and removed from active searches.
              </p>
            </div>
          )}

          {selectedStatus === "need_help" && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-sm">
              <p className="font-semibold text-orange-900 mb-1">
                We&apos;re here to help!
              </p>
              <p className="text-orange-700">
                Our team will reach out to you within 24 hours to discuss how we can assist you.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedStatus || submitting}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Update Status
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StatusUpdateDialog;
